import {
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { IMenhirBenefitSlug } from '@/common/integrations/menhir/employee/benefits/benefits.types';
import Modal from '@/web/components/Modal';
import { Title } from '@/web/components/Typography';
import { Button } from '@coverflex-tech/hypernova';
import classNames from 'classnames';
import css from './learning-centre-modal.module.css';

interface IProps {
  title?: string;
  subtitle?: ReactNode;
  context?: IMenhirBenefitSlug | 'savings';
  icon?: ReactNode;
  steps: ReactNode[];
  onClose: () => void;
  className?: string;
  hasHeaderSection?: boolean;
}

export const LearningCentreModal = ({
  title,
  subtitle,
  context,
  icon,
  steps,
  onClose,
  className,
  hasHeaderSection = true,
}: IProps) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const nextRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (nextRef.current) {
      nextRef.current.focus();
    }
  }, []);

  const eventBenefit = context ?? 'benefits';

  const handleNext = useCallback(
    (evt: MouseEvent | KeyboardEvent) => {
      evt.stopPropagation();
      if (currentStep === steps.length - 1) {
        AnalyticsService.trackCustomEvent('learning_centre_modal_close', {
          source: 'gotit_button',
          benefit_category: eventBenefit,
        });
        onClose();
        return;
      }

      AnalyticsService.trackCustomEvent(
        'learning_centre_modal_next_button_clicked',
        {
          benefit_category: eventBenefit,
        }
      );
      setCurrentStep(currentStep + 1);
    },
    [currentStep, steps, onClose, eventBenefit]
  );

  const handleBack = useCallback(
    (evt: MouseEvent | KeyboardEvent) => {
      evt.stopPropagation();
      setCurrentStep(currentStep > 0 ? currentStep - 1 : currentStep);
    },
    [currentStep]
  );

  const handleModalClose = () => {
    AnalyticsService.trackCustomEvent('learning_centre_modal_close', {
      source: 'close_button',
      benefit_category: eventBenefit,
    });
    onClose();
  };

  useEffect(() => {
    AnalyticsService.trackCustomEvent('learning_centre_modal_open', {
      benefit_category: eventBenefit,
    });
  }, [eventBenefit]);

  return (
    <Modal
      className={css.host}
      onClose={handleModalClose}
      onKeyPress={(e) => {
        if (e.key === 'ArrowRight') {
          handleNext(e);
        }
        if (e.key === 'ArrowLeft') {
          handleBack(e);
        }
      }}
    >
      {hasHeaderSection && (
        <div className={css.header}>
          <div className={css.icon}>{icon}</div>
          <Title size="small" className={css.title}>
            {title}
          </Title>
          {subtitle && (
            <div className={css.subtitle}>
              <Trans>{subtitle}</Trans>
            </div>
          )}
        </div>
      )}
      <div
        className={classNames(
          css.content,
          {
            [css.withFooter!]: steps.length > 1,
            [css.rounded!]: !hasHeaderSection,
          },
          className
        )}
      >
        {steps[currentStep]}
      </div>
      {steps.length > 1 && (
        <div
          className={classNames(css.footer, {
            [css.footerSpacing!]: hasHeaderSection,
          })}
        >
          <div className={css.navigationSteps}>
            {steps.map((_, idx) => (
              <div
                key={`step-${idx}`}
                className={classNames(css.navigationStep, {
                  [css.active!]: currentStep === idx,
                })}
              />
            ))}
          </div>

          <div className={css.actions}>
            {currentStep > 0 && (
              <Button variant="secondary" size="sm" onClick={handleBack}>
                {t('learning_centre.back')}
              </Button>
            )}
            <Button onClick={handleNext} ref={nextRef} size="sm">
              {t(
                `learning_centre.${
                  currentStep === steps.length - 1 ? 'close' : 'next'
                }`
              )}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
